import React, { useState } from 'react';
import { DataGrid } from '@material-ui/data-grid';
import  CustomPagination  from '../../../znotusedcurrently/orders/MaterialUIOrdersTable.jsx2';
import { makeStyles } from '@material-ui/core/styles';
import ApplyRounding from '../../../utils/math/ApplyRounding';
import TwoDecimalFormatter from '../../../utils/math/TwoDecimalFormatter';
import { Grid, Typography } from '@material-ui/core';
import TranslateTradierOption from '../../../utils/componentutils/TranslateTradierOption';
import determineSide from '../../../utils/positions/DetermineSide';

export const candleSequenceTranslations = {
  BREAK_HIGH: '2u',
  BREAK_LOW: '2d',
  FAIL_TO_BREAK_HIGH: 'fbh',
  FAIL_TO_BREAK_LOW: 'fbl',
  INSIDE_CANDLE: '1',
  OUTSIDE_CANDLE: '3',
  OUTSIDE_CANDLE_UP: '3u',
  OUTSIDE_CANDLE_DOWN: '3d',
  BREAK_CANDLE: '2',
  '2u': 'BREAK_HIGH',
  '2d': 'BREAK_LOW',
  fbh: 'FAIL_TO_BREAK_HIGH',
  fbl: 'FAIL_TO_BREAK_LOW',
  1: 'INSIDE_CANDLE',
  3: 'OUTSIDE_CANDLE',
  '3u': 'OUTSIDE_CANDLE_UP',
  '3d': 'OUTSIDE_CANDLE_DOWN',
  2: 'BREAK_CANDLE',
};


const useStyles = makeStyles((theme) => ({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    fontSize: 8,
  },
}));

function getUnderlying(sym) {
  if (sym.length > 6) {
    let index = 0;
    let underlying = '';
    for (let i in sym) {
      let char = sym[i];
      if (!(!isNaN(parseFloat(char)) && isFinite(char))) {
        //ie is a character
        index++;
      } else {
        underlying = sym.substring(0, index - 1);
      }
    }
    return underlying;
  }
  return sym;
}

function isLongTrade(closedPosition) {
  let symbol = closedPosition.symbol;
  if (symbol.length > 6) {
    if (symbol.substring(6).includes('C')) {
      return (
        closedPosition.side === 'buy_to_open' || closedPosition.side === 'buy'
      );
    } else if (symbol.substring(6).includes('P')) {
      return (
        closedPosition.side === 'sell_to_open' ||
        closedPosition.side === 'sell_short'
      );
    }
  }
  return closedPosition.side === 'buy';
}

function isShortTrade(closedPosition) {
  let symbol = closedPosition.symbol;
  if (symbol.length > 6) {
    // console.log(symbol.substring(6));
    if (symbol.substring(6).includes('P')) {
      // console.log("includes P")
      // console.log(closedPosition.side)
      return (
        closedPosition.side === 'buy_to_open' || closedPosition.side === 'buy'
      );
    } else if (symbol.substring(6).includes('C')) {
      return (
        closedPosition.side === 'sell_to_open' ||
        closedPosition.side === 'sell_short'
      );
    }
  }
  return closedPosition.side === 'sell_short';
}

function CpByUnderlyingSymbol(values) {
  let closedPositionsByUnderlyingSymbol = {};
  for (let id in values) {
    let closedPosition = values[id];
    let symbol = getUnderlying(closedPosition.symbol);
    if (closedPositionsByUnderlyingSymbol[symbol] === undefined) {
      closedPositionsByUnderlyingSymbol[symbol] = {
        pnl: 0,
        cost_basis: 0,
        percent_pnl: 0,
        tradeCount: 0,
        id: symbol,
        symbol: symbol,
        isLong: false,
        isShort: false,
        side: '',
      };
    }
    let cumulativeTrades = closedPositionsByUnderlyingSymbol[symbol];
    let pnl = cumulativeTrades.pnl;
    let cost_basis = cumulativeTrades.cost_basis;
    //let percent_pnl = cumulativeTrades.percent_pnl;
    let tradeCount = cumulativeTrades.tradeCount;
    cumulativeTrades.pnl = pnl + parseFloat(closedPosition.pnl);
    cumulativeTrades.cost_basis =
      cost_basis + parseFloat(closedPosition.cost_basis);
    cumulativeTrades.percent_pnl =
      ((parseFloat(cumulativeTrades.cost_basis) +
        parseFloat(cumulativeTrades.pnl)) /
        parseFloat(cumulativeTrades.cost_basis) -
        1) *
      100;
    cumulativeTrades.tradeCount = tradeCount + 1;
    if (isLongTrade(closedPosition) && !cumulativeTrades.isLong) {
      cumulativeTrades.isLong = true;
    }
    if (isShortTrade(closedPosition) && !cumulativeTrades.isShort) {
      cumulativeTrades.isShort = true;
    }
    if (cumulativeTrades.isLong && cumulativeTrades.isShort) {
      cumulativeTrades.side = 'BOTH';
    } else if (cumulativeTrades.isLong) {
      cumulativeTrades.side = 'LONG';
    } else if (cumulativeTrades.isShort) {
      cumulativeTrades.side = 'SHORT';
    }
  }
  return Object.values(closedPositionsByUnderlyingSymbol);
}

const CpBySymbol = (values) => {
  let closedPositionsBySymbol = {};
  for (let id in values) {
    let closedPosition = values[id];
    if (closedPositionsBySymbol[closedPosition.symbol] === undefined) {
      closedPositionsBySymbol[closedPosition.symbol] = {
        pnl: 0,
        cost_basis: 0,
        percent_pnl: 0,
        tradeCount: 0,
        id: closedPosition.symbol,
        symbol: closedPosition.symbol,
        isLong: false,
        isShort: false,
        side: '',
      };
    }
    let cumulativeTrades = closedPositionsBySymbol[closedPosition.symbol];
    let pnl = cumulativeTrades.pnl;
    let cost_basis = cumulativeTrades.cost_basis;
    //let percent_pnl = cumulativeTrades.percent_pnl;
    let tradeCount = cumulativeTrades.tradeCount;
    cumulativeTrades.pnl = pnl + parseFloat(closedPosition.pnl);
    cumulativeTrades.cost_basis =
      cost_basis + parseFloat(closedPosition.cost_basis);
    cumulativeTrades.percent_pnl =
      ((parseFloat(cumulativeTrades.cost_basis) +
        parseFloat(cumulativeTrades.pnl)) /
        parseFloat(cumulativeTrades.cost_basis) -
        1) *
      100;
    cumulativeTrades.tradeCount = tradeCount + 1;
    if (isLongTrade(closedPosition) && !cumulativeTrades.isLong) {
      cumulativeTrades.isLong = true;
    }
    if (isShortTrade(closedPosition) && !cumulativeTrades.isShort) {
      cumulativeTrades.isShort = true;
    }
    if (cumulativeTrades.isLong && cumulativeTrades.isShort) {
      cumulativeTrades.side = 'BOTH';
    } else if (cumulativeTrades.isLong) {
      cumulativeTrades.side = 'LONG';
    } else if (cumulativeTrades.isShort) {
      cumulativeTrades.side = 'SHORT';
    }
  }
  return Object.values(closedPositionsBySymbol);
};

const CpByTsId = (values) => {
  let closedPositionsByTsId = {};
  for (let id in values) {
    let closedPosition = values[id];
    if (closedPositionsByTsId[closedPosition.tradingSystemId] === undefined) {
      closedPositionsByTsId[closedPosition.tradingSystemId] = {
        pnl: 0,
        cost_basis: 0,
        percent_pnl: 0,
        tradeCount: 0,
        tradingSystemId: closedPosition.tradingSystemId,
        strategy: closedPosition.strategy,
        id: closedPosition.tradingSystemId,
        symbol: getUnderlying(closedPosition.symbol),
        day: closedPosition.date_acquired.split('T')[0],
        isLong: false,
        isShort: false,
        side: '',
        tradePlanTemplate: closedPosition.tradePlanTemplate,
      };
    }
    let cumulativeTrades =
      closedPositionsByTsId[closedPosition.tradingSystemId];
    let pnl = cumulativeTrades.pnl;
    let cost_basis = cumulativeTrades.cost_basis;
    //let percent_pnl = cumulativeTrades.percent_pnl;
    let tradeCount = cumulativeTrades.tradeCount;
    cumulativeTrades.pnl = pnl + parseFloat(closedPosition.pnl);
    cumulativeTrades.cost_basis =
      cost_basis + parseFloat(closedPosition.cost_basis);
    cumulativeTrades.percent_pnl =
      ((parseFloat(cumulativeTrades.cost_basis) +
        parseFloat(cumulativeTrades.pnl)) /
        parseFloat(cumulativeTrades.cost_basis) -
        1) *
      100;
    cumulativeTrades.tradeCount = tradeCount + 1;
    if (isLongTrade(closedPosition) && !cumulativeTrades.isLong) {
      cumulativeTrades.isLong = true;
    }
    if (isShortTrade(closedPosition) && !cumulativeTrades.isShort) {
      cumulativeTrades.isShort = true;
    }
    if (cumulativeTrades.isLong && cumulativeTrades.isShort) {
      cumulativeTrades.side = 'BOTH';
    } else if (cumulativeTrades.isLong) {
      cumulativeTrades.side = 'LONG';
    } else if (cumulativeTrades.isShort) {
      cumulativeTrades.side = 'SHORT';
    }
  }
  return Object.values(closedPositionsByTsId);
};

const cpByDay = (values) => {
  let closedPositionsByTemplate = {};
  for (let id in values) {
    let closedPosition = values[id];
    let day = closedPosition.date_acquired.split('T')[0];
    if (closedPositionsByTemplate[day] === undefined) {
      closedPositionsByTemplate[day] = {
        pnl: 0,
        cost_basis: 0,
        percent_pnl: 0,
        tradeCount: 0,
        /* tradingSystemId: closedPosition.tradingSystemId,
                 strategy: closedPosition.strategy,*/
        id: day,
        //symbol: getUnderlying(closedPosition.symbol),
        //day: closedPosition.date_acquired.split("T")[0],
        isLong: false,
        isShort: false,
        side: '',
        day: day,
      };
    }
    let cumulativeTrades = closedPositionsByTemplate[day];
    let pnl = cumulativeTrades.pnl;
    let cost_basis = cumulativeTrades.cost_basis;
    //let percent_pnl = cumulativeTrades.percent_pnl;
    let tradeCount = cumulativeTrades.tradeCount;
    cumulativeTrades.pnl = pnl + parseFloat(closedPosition.pnl);
    cumulativeTrades.cost_basis =
      cost_basis + parseFloat(closedPosition.cost_basis);
    cumulativeTrades.percent_pnl =
      ((parseFloat(cumulativeTrades.cost_basis) +
        parseFloat(cumulativeTrades.pnl)) /
        parseFloat(cumulativeTrades.cost_basis) -
        1) *
      100;
    cumulativeTrades.tradeCount = tradeCount + 1;
    if (isLongTrade(closedPosition) && !cumulativeTrades.isLong) {
      cumulativeTrades.isLong = true;
    }
    if (isShortTrade(closedPosition) && !cumulativeTrades.isShort) {
      cumulativeTrades.isShort = true;
    }
    if (cumulativeTrades.isLong && cumulativeTrades.isShort) {
      cumulativeTrades.side = 'BOTH';
    } else if (cumulativeTrades.isLong) {
      cumulativeTrades.side = 'LONG';
    } else if (cumulativeTrades.isShort) {
      cumulativeTrades.side = 'SHORT';
    }
  }
  return Object.values(closedPositionsByTemplate);
};

const CpByTemplate = (values) => {
  let closedPositionsByTemplate = {};
  for (let id in values) {
    let closedPosition = values[id];
    if (
      closedPosition.tradePlanTemplate === undefined ||
      closedPosition.tradePlanTemplate === null ||
      closedPosition.tradePlanTemplate === '' ||
      closedPosition.tradePlanTemplate === 'null'
    ) {
      continue;
    }
    if (
      closedPositionsByTemplate[closedPosition.tradePlanTemplate] === undefined
    ) {
      closedPositionsByTemplate[closedPosition.tradePlanTemplate] = {
        pnl: 0,
        cost_basis: 0,
        percent_pnl: 0,
        tradeCount: 0,
        /* tradingSystemId: closedPosition.tradingSystemId,
                 strategy: closedPosition.strategy,*/
        id: closedPosition.tradePlanTemplate,
        //symbol: getUnderlying(closedPosition.symbol),
        //day: closedPosition.date_acquired.split("T")[0],
        isLong: false,
        isShort: false,
        side: '',
        tradePlanTemplate: closedPosition.tradePlanTemplate,
      };
    }
    let cumulativeTrades =
      closedPositionsByTemplate[closedPosition.tradePlanTemplate];
    let pnl = cumulativeTrades.pnl;
    let cost_basis = cumulativeTrades.cost_basis;
    //let percent_pnl = cumulativeTrades.percent_pnl;
    let tradeCount = cumulativeTrades.tradeCount;
    cumulativeTrades.pnl = pnl + parseFloat(closedPosition.pnl);
    cumulativeTrades.cost_basis =
      cost_basis + parseFloat(closedPosition.cost_basis);
    cumulativeTrades.percent_pnl =
      ((parseFloat(cumulativeTrades.cost_basis) +
        parseFloat(cumulativeTrades.pnl)) /
        parseFloat(cumulativeTrades.cost_basis) -
        1) *
      100;
    cumulativeTrades.tradeCount = tradeCount + 1;
    if (isLongTrade(closedPosition) && !cumulativeTrades.isLong) {
      cumulativeTrades.isLong = true;
    }
    if (isShortTrade(closedPosition) && !cumulativeTrades.isShort) {
      cumulativeTrades.isShort = true;
    }
    if (cumulativeTrades.isLong && cumulativeTrades.isShort) {
      cumulativeTrades.side = 'BOTH';
    } else if (cumulativeTrades.isLong) {
      cumulativeTrades.side = 'LONG';
    } else if (cumulativeTrades.isShort) {
      cumulativeTrades.side = 'SHORT';
    }
  }
  return Object.values(closedPositionsByTemplate);
};

export default function MaterialUiClosedPositionsOptions({ values }) {
  const [closedPositionsType, setClosedPositionsType] = useState('byPosition');
  //console.log("render cp")
  return (
    <>
      <ClosedPositionsHeader
        values={values}
        closedPositionsType={closedPositionsType}
        setClosedPositionsType={setClosedPositionsType}
      />
      {closedPositionsType === 'byPosition' ? (
        <MaterialUiClosedPositionsTable values={values} />
      ) : closedPositionsType === 'bySymbol' ? (
        <ClosedPositionsSymbolTable valuesArray={CpBySymbol(values)} />
      ) : closedPositionsType === 'byUnderlying' ? (
        <ClosedPositionsSymbolTable
          valuesArray={CpByUnderlyingSymbol(values)}
        />
      ) : closedPositionsType === 'byTradePlan' ? (
        <ClosedPositionsByTsTable valuesArray={CpByTsId(values)} />
      ) : closedPositionsType === 'byTemplate' ? (
        <ClosedPositionsByTemplateTable valuesArray={CpByTemplate(values)} />
      ) : closedPositionsType === 'byDay' ? (
        <ClosedPositionsByDayTable valuesArray={cpByDay(values)} />
      ) : null}
    </>
  );
}

function ClosedPositionsHeader({
  values,
  closedPositionsType,
  setClosedPositionsType,
}) {
  let sumPnl = 0;
  let sumCostBasis = 0;
  let closedPositionsMsg = values;
  //console.log(values);
  let closedPositions = [];
  for (let id in closedPositionsMsg) {
    let closedPosition = closedPositionsMsg[id];
    closedPosition['id'] = id;
    closedPositions.push(closedPosition);
    closedPosition.percent_pnl = parseFloat(closedPosition.percent_pnl);
    closedPosition.cost_basis = parseFloat(closedPosition.cost_basis);
    sumCostBasis += closedPosition.cost_basis;

    closedPosition.pnl = parseFloat(closedPosition.pnl);
    sumPnl += closedPosition.pnl;
  }

  return (
    <div style={{ height: 37, width: '100%', textAlign: 'left' }}>
      <Grid container>
        <Grid item xs={12}>
          <button
            style={{
              fontSize: 10,
              height: 18,
              color: 'white',
              backgroundColor:
                closedPositionsType === 'byPosition' ? 'blue' : 'black',
            }}
            onClick={(ev) => setClosedPositionsType('byPosition')}
          >
            by position
          </button>
          <button
            style={{
              fontSize: 10,
              height: 18,
              color: 'white',
              backgroundColor:
                closedPositionsType === 'bySymbol' ? 'blue' : 'black',
            }}
            onClick={(ev) => setClosedPositionsType('bySymbol')}
          >
            by symbol
          </button>
          <button
            style={{
              fontSize: 10,
              height: 18,
              color: 'white',
              backgroundColor:
                closedPositionsType === 'byUnderlying' ? 'blue' : 'black',
            }}
            onClick={(ev) => setClosedPositionsType('byUnderlying')}
          >
            by underlying
          </button>
          <button
            style={{
              fontSize: 10,
              height: 18,
              color: 'white',
              backgroundColor:
                closedPositionsType === 'byTradePlan' ? 'blue' : 'black',
            }}
            onClick={(ev) => setClosedPositionsType('byTradePlan')}
          >
            by trade plan
          </button>
          <button
            style={{
              fontSize: 10,
              height: 18,
              color: 'white',
              backgroundColor:
                closedPositionsType === 'byTemplate' ? 'blue' : 'black',
            }}
            onClick={(ev) => setClosedPositionsType('byTemplate')}
          >
            by template
          </button>
          <button
            style={{
              fontSize: 10,
              height: 18,
              color: 'white',
              backgroundColor:
                closedPositionsType === 'byDay' ? 'blue' : 'black',
            }}
            onClick={(ev) => setClosedPositionsType('byDay')}
          >
            by day
          </button>
        </Grid>
        <Grid item xs={12}>
          <Typography style={{ fontSize: 10, textAlign: 'left' }}>
            Total Cost Basis: ${ApplyRounding(sumCostBasis, 2)}. Total Pnl: $
            {ApplyRounding(sumPnl, 2)}. Pct:{' '}
            {ApplyRounding(
              ((sumCostBasis + sumPnl) / sumCostBasis - 1) * 100,
              2
            )}
            %
          </Typography>
        </Grid>
      </Grid>
    </div>
  );
}

function ClosedPositionsSymbolTable({ valuesArray }) {
  const classes = useStyles();
  const columns = [
    {
      headerName: 'symbol',
      field: 'symbol',
      width: 100,
      //sortable: false,
      valueFormatter: (params) => {
        let symbol = params.value;
        if (symbol.length > 6) {
          //ie is option
          return TranslateTradierOption(symbol);
        } else {
          return symbol;
        }
      },
    },
    {
      headerName: 'direction',
      field: 'side',
      sortable: false,
      width: 48, //sortable: false,
      //valueFormatter: (params) => determineSide(params.value),
    },
    {
      headerName: 'pnl',
      field: 'pnl',
      width: 70, //sortable: false,
      renderCell: (params) => (
        <Typography
          style={{
            fontSize: 8,
            color: parseFloat(params.value) > 0 ? 'green' : 'red',
          }}
        >
          {TwoDecimalFormatter(parseFloat(params.value))}
        </Typography>
      ),
    },
    {
      headerName: '%', //sortable: false,
      field: 'percent_pnl',
      width: 62,
      renderCell: (params) => (
        <Typography
          style={{
            fontSize: 8,
            color: parseFloat(params.value) > 0 ? 'green' : 'red',
          }}
        >
          {TwoDecimalFormatter(parseFloat(params.value))}%
        </Typography>
      ),
    },
    {
      headerName: 'cost',
      field: 'cost_basis', //sortable: false,
      width: 75,
      valueFormatter: (params) => TwoDecimalFormatter(parseFloat(params.value)),
    },
    {
      headerName: 'trades',
      field: 'tradeCount', //sortable: false,
      //valueFormatter: (params) => params.value.split("T")[1].split("-")[0],
      width: 80,
      //  width: 80,
    },
  ];
  //console.log(sumCostBasis);
  //console.log(sumPnl);
  return (
    <>
      <div style={{ height: 410, width: '100%' }}>
        <DataGrid
          className={classes.root}
          rows={valuesArray}
          columns={columns}
          rowHeight={20}
          headerHeight={25}
          disableSelectionOnClick
          pageSize={20}
          disableColumnMenu
          autoPageSize={true}
          scrollbarSize={1}
          components={{
            pagination: CustomPagination,
          }}
        />
      </div>
    </>
  );
}

function ClosedPositionsByTsTable({ valuesArray }) {
  const classes = useStyles();
  const columns = [
    {
      headerName: 'symbol',
      field: 'symbol',
      width: 100,
      //sortable: false,
      valueFormatter: (params) => {
        let symbol = params.value;
        if (symbol.length > 6) {
          //ie is option
          return TranslateTradierOption(symbol);
        } else {
          return symbol;
        }
      },
    },
    {
      headerName: 'setup',
      field: 'strategy', //sortable: false,
      //valueFormatter: (params) => params.value.split("T")[1].split("-")[0],
      width: 80,
      //  width: 80,
    },
    {
      headerName: 'direction',
      field: 'side',
      sortable: false,
      width: 48, //sortable: false,
      // valueFormatter: (params) => determineSide(params.value),
    },
    {
      headerName: 'pnl',
      field: 'pnl',
      width: 70, //sortable: false,
      renderCell: (params) => (
        <Typography
          style={{
            fontSize: 8,
            color: parseFloat(params.value) > 0 ? 'green' : 'red',
          }}
        >
          {TwoDecimalFormatter(parseFloat(params.value))}
        </Typography>
      ),
    },
    {
      headerName: '%', //sortable: false,
      field: 'percent_pnl',
      width: 62,
      renderCell: (params) => (
        <Typography
          style={{
            fontSize: 8,
            color: parseFloat(params.value) > 0 ? 'green' : 'red',
          }}
        >
          {TwoDecimalFormatter(parseFloat(params.value))}%
        </Typography>
      ),
    },
    {
      headerName: 'cost',
      field: 'cost_basis', //sortable: false,
      width: 75,
      valueFormatter: (params) => TwoDecimalFormatter(parseFloat(params.value)),
    },
    {
      headerName: 'trades',
      field: 'tradeCount', //sortable: false,
      //valueFormatter: (params) => params.value.split("T")[1].split("-")[0],
      width: 80,
      //  width: 80,
    },
    {
      headerName: 'template',
      field: 'tradePlanTemplate',
    },
    {
      headerName: 'day',
      field: 'day', //sortable: false,
      //valueFormatter: (params) =>,
      width: 88,
      //  width: 80,
    },
  ];
  //console.log(sumCostBasis);
  //console.log(sumPnl);
  return (
    <>
      <div style={{ height: 410, width: '100%' }}>
        <DataGrid
          className={classes.root}
          rows={valuesArray}
          columns={columns}
          rowHeight={20}
          headerHeight={25}
          disableSelectionOnClick
          pageSize={20}
          disableColumnMenu
          autoPageSize={true}
          scrollbarSize={1}
          components={{
            pagination: CustomPagination,
          }}
        />
      </div>
    </>
  );
}

function ClosedPositionsByTemplateTable({ valuesArray }) {
  const classes = useStyles();
  const columns = [
    /*{
            headerName: "symbol",
            field: "symbol",
            width: 100,
            //sortable: false,
            valueFormatter: (params) => {
                let symbol = params.value;
                if (symbol.length > 6) {
                    //ie is option
                    return TranslateTradierOption(symbol);
                } else {
                    return symbol;
                }
            },
        },*/
    /*  {
              headerName: "setup",
              field: "strategy", //sortable: false,
              //valueFormatter: (params) => params.value.split("T")[1].split("-")[0],
              width: 80,
              //  width: 80,
          },*/
    {
      headerName: 'template',
      field: 'tradePlanTemplate',
    },
    {
      headerName: 'direction',
      field: 'side',
      sortable: false,
      width: 48, //sortable: false,
      // valueFormatter: (params) => determineSide(params.value),
    },
    {
      headerName: 'pnl',
      field: 'pnl',
      width: 70, //sortable: false,
      renderCell: (params) => (
        <Typography
          style={{
            fontSize: 8,
            color: parseFloat(params.value) > 0 ? 'green' : 'red',
          }}
        >
          {TwoDecimalFormatter(parseFloat(params.value))}
        </Typography>
      ),
    },
    {
      headerName: '%', //sortable: false,
      field: 'percent_pnl',
      width: 62,
      renderCell: (params) => (
        <Typography
          style={{
            fontSize: 8,
            color: parseFloat(params.value) > 0 ? 'green' : 'red',
          }}
        >
          {TwoDecimalFormatter(parseFloat(params.value))}%
        </Typography>
      ),
    },
    {
      headerName: 'cost',
      field: 'cost_basis', //sortable: false,
      width: 75,
      valueFormatter: (params) => TwoDecimalFormatter(parseFloat(params.value)),
    },
    {
      headerName: 'trades',
      field: 'tradeCount', //sortable: false,
      //valueFormatter: (params) => params.value.split("T")[1].split("-")[0],
      width: 80,
      //  width: 80,
    },
    /* {
             headerName: "day",
             field: "day", //sortable: false,
             //valueFormatter: (params) =>,
             width: 88,
             //  width: 80,
         }*/
  ];
  //console.log(sumCostBasis);
  //console.log(sumPnl);
  return (
    <>
      <div style={{ height: 410, width: '100%' }}>
        <DataGrid
          className={classes.root}
          rows={valuesArray}
          columns={columns}
          rowHeight={20}
          headerHeight={25}
          disableSelectionOnClick
          pageSize={20}
          disableColumnMenu
          autoPageSize={true}
          scrollbarSize={1}
          components={{
            pagination: CustomPagination,
          }}
        />
      </div>
    </>
  );
}

function ClosedPositionsByDayTable({ valuesArray }) {
  const classes = useStyles();
  const columns = [
    /*{
            headerName: "symbol",
            field: "symbol",
            width: 100,
            //sortable: false,
            valueFormatter: (params) => {
                let symbol = params.value;
                if (symbol.length > 6) {
                    //ie is option
                    return TranslateTradierOption(symbol);
                } else {
                    return symbol;
                }
            },
        },*/
    /*  {
              headerName: "setup",
              field: "strategy", //sortable: false,
              //valueFormatter: (params) => params.value.split("T")[1].split("-")[0],
              width: 80,
              //  width: 80,
          },*/
    {
      headerName: 'day',
      field: 'day',
    },
    {
      headerName: 'direction',
      field: 'side',
      sortable: false,
      width: 48, //sortable: false,
      // valueFormatter: (params) => determineSide(params.value),
    },
    {
      headerName: 'pnl',
      field: 'pnl',
      width: 70, //sortable: false,
      renderCell: (params) => (
        <Typography
          style={{
            fontSize: 8,
            color: parseFloat(params.value) > 0 ? 'green' : 'red',
          }}
        >
          {TwoDecimalFormatter(parseFloat(params.value))}
        </Typography>
      ),
    },
    {
      headerName: '%', //sortable: false,
      field: 'percent_pnl',
      width: 62,
      renderCell: (params) => (
        <Typography
          style={{
            fontSize: 8,
            color: parseFloat(params.value) > 0 ? 'green' : 'red',
          }}
        >
          {TwoDecimalFormatter(parseFloat(params.value))}%
        </Typography>
      ),
    },
    {
      headerName: 'cost',
      field: 'cost_basis', //sortable: false,
      width: 75,
      valueFormatter: (params) => TwoDecimalFormatter(parseFloat(params.value)),
    },
    {
      headerName: 'trades',
      field: 'tradeCount', //sortable: false,
      //valueFormatter: (params) => params.value.split("T")[1].split("-")[0],
      width: 80,
      //  width: 80,
    },
    /* {
             headerName: "day",
             field: "day", //sortable: false,
             //valueFormatter: (params) =>,
             width: 88,
             //  width: 80,
         }*/
  ];
  //console.log(sumCostBasis);
  //console.log(sumPnl);
  return (
    <>
      <div style={{ height: 410, width: '100%' }}>
        <DataGrid
          className={classes.root}
          rows={valuesArray}
          columns={columns}
          rowHeight={20}
          headerHeight={25}
          disableSelectionOnClick
          pageSize={20}
          disableColumnMenu
          autoPageSize={true}
          scrollbarSize={1}
          components={{
            pagination: CustomPagination,
          }}
        />
      </div>
    </>
  );
}

function MaterialUiClosedPositionsTable({ values }) {
  //let sumPnl = 0;
  //let sumCostBasis = 0;
  const classes = useStyles();
  const columns = [
    {
      headerName: 'symbol',
      field: 'symbol',
      width: 94,
      //sortable: false,
      valueFormatter: (params) => {
        let symbol = params.value;
        if (symbol.length > 6) {
          //ie is option
          return TranslateTradierOption(symbol);
        } else {
          return symbol;
        }
      },
    },
    {
      headerName: 'side',
      field: 'side',
      sortable: false,
      width: 48, //sortable: false,
      valueFormatter: (params) => determineSide(params.value),
    },
    {
      headerName: 'pnl',
      field: 'pnl',
      width: 68, //sortable: false,
      renderCell: (params) => (
        <Typography
          style={{
            fontSize: 8,
            color: parseFloat(params.value) > 0 ? 'green' : 'red',
          }}
        >
          {TwoDecimalFormatter(parseFloat(params.value))}
        </Typography>
      ),
    },
    {
      headerName: '%', //sortable: false,
      field: 'percent_pnl',
      width: 62,
      renderCell: (params) => (
        <Typography
          style={{
            fontSize: 8,
            color: parseFloat(params.value) > 0 ? 'green' : 'red',
          }}
        >
          {TwoDecimalFormatter(parseFloat(params.value))}%
        </Typography>
      ),
    },
    {
      headerName: 'cost',
      field: 'cost_basis', //sortable: false,
      width: 72,
      valueFormatter: (params) => TwoDecimalFormatter(parseFloat(params.value)),
    },
    {
      headerName: 'qty',
      field: 'quantity',
      width: 44,
      sortable: false,
      valueFormatter: (params) => ApplyRounding(parseFloat(params.value), 0),
    },
    {
      headerName: 'opened',
      field: 'date_acquired', //sortable: false,
      valueFormatter: (params) => params.value.split('T')[1].split('-')[0],
      width: 84,
      //  width: 80,
    },
    {
      headerName: 'exited', //sortable: false,
      field: 'date_exited',
      valueFormatter: (params) => params.value.split('T')[1].split('-')[0],
      width: 80,
      //   width: 80,
    },
    {
      headerName: 'setup',
      sortable: false,
      field: 'strategy',
    },
    {
      headerName: 'setup description',
      field: 'strategyDescription',
      valueFormatter: (params) => {
        if (params.value && params.value.includes("-")) {
          let setupDescription = params.value;
          console.log(setupDescription);
          if (params.row.strategy === 'CANDLE_PATTERN') {
            console.log(params);
            let pattern = setupDescription.split('-');
            let description = '';
            for (let i = 0; i < pattern.length; i++) {
              let sequence = pattern[i];
              description += candleSequenceTranslations[sequence];
              if (i !== pattern.length - 1) {
                description += '-';
              }
            }
            return description;
          }
        }
        return params.value;
      },
    },
    {
      headerName: 'template',
      field: 'tradePlanTemplate',
    },
    {
      headerName: 'accountId',
      sortable: false,
      field: 'accountId',
    },
    {
      headerName: 'day',
      field: 'day', //sortable: false,
      //valueFormatter: (params) =>,
      width: 88,
      //  width: 80,
    },
  ];
  let closedPositionsMsg = values;
  let closedPositions = [];
  for (let id in closedPositionsMsg) {
    let closedPosition = closedPositionsMsg[id];
    closedPosition['id'] = id;
    closedPositions.push(closedPosition);
    closedPosition.percent_pnl = parseFloat(closedPosition.percent_pnl);
    closedPosition.cost_basis = parseFloat(closedPosition.cost_basis);
    //sumCostBasis += closedPosition.cost_basis;
    closedPosition.day = closedPosition.date_acquired.split('T')[0];
    closedPosition.pnl = parseFloat(closedPosition.pnl);
    //sumPnl += closedPosition.pnl;
  }
  //console.log(sumCostBasis);
  //console.log(sumPnl);
  return (
    <>
      <div style={{ height: 410, width: '100%' }}>
        <DataGrid
          className={classes.root}
          rows={closedPositions}
          columns={columns}
          rowHeight={20}
          headerHeight={25}
          disableSelectionOnClick
          pageSize={20}
          disableColumnMenu
          autoPageSize={true}
          scrollbarSize={1}
          components={{
            pagination: CustomPagination,
          }}
        />
      </div>
    </>
  );
}
